/**
 * SowEmbedPage — the chromeless SOW embed route (ADR-080 Amendment 2).
 *
 * This IS the FEP's UNAUTHENTICATED public mount, presented per §A2.2/§A2.3:
 * the single SOW journey rendered standalone — no portal nav, no COG, no
 * chrome — inside a hardened cross-origin <iframe> framed by the `www`
 * marketing-site modal shell (nexa-website sow-modal.js). The visitor never
 * leaves nexanomics.com; the iframe is an invisible security sandbox, not a
 * UX page. It replaces the retired full-page /sign-up entry (SignUpPage).
 *
 * Hardening (§A2.3):
 *  - `frame-ancestors` CSP on this route is locked to the `www` origin(s)
 *    only — see portal-subscriber/public/_headers.
 *  - postMessage is origin-validated BOTH sides via sow-embed-bridge; the
 *    schema is explicit (hello/init handshake, then resize/close/success).
 *  - A top-level (unframed) visit is redirected to the marketing site — the
 *    embed is not a navigable page (§A2.2 "no app.nexanomics.com page").
 *
 * Gate-driven content (§A2.4 — Am.1 enforcement unchanged): gate closed →
 * the beta-invite face (BetaGate, copy per A2.6); gate open → the card-first
 * sign-up journey (§2.1). Enforcement stays server-side (edge Worker + 403
 * backstop); this page is only the face of that wall.
 *
 * URL params (passed on the iframe src by the shell, never a www page URL):
 *   ?promo=<code>   → carried on the closed-gate beta-interest capture only (A2.5/D3)
 *   ?plan=…         → IGNORED since 5 October 2026 (see below)
 *
 * ⭐⭐ D1 (Jeff, 5 October 2026) — SIGN-UP CAPTURES IDENTITY ONLY. "Plan selection including
 * terms, specials, discounts are all the SoW, as is the Stripe payment (currently stubbed)."
 * This form used to carry a plan grid, a promo pill and send `selectedTier`/`promoCode`; that
 * supersedes ADR-073's capture point (ADR-073 Amendment 4). The form now asks who the customer is
 * and where their business is registered, and says plans and payment come next. signup-api
 * REFUSES a plan, promo or card field (400 `plan_selected_in_sow`), so this cannot drift back.
 *
 * ⭐ D3 — the country list is the public-sign-up SHORTLIST (`lib/signup-countries.ts`), not the
 * 249-country served map; an unlisted country is told so and offered the interest path.
 *
 * ⭐⭐ THE SOW MATRIX, RE-USED — NOT RE-ENGINEERED (ADR-073 Amendment 5 §A5.6, 5 October 2026).
 * Jeff, on the three-screen wizard this page used to hand-roll: "what I want is the SOW matrix
 * re-used as that is the purpose of creating it in the first place. I do not want a re-engineered
 * solution." So after the sign-up form writes the `SignupLead`, this page mounts the ENGINE —
 * `OBWWizard`, journey `sign-up`, account-less (`accountId: ''`), exactly as `StaffObwModal` mounts
 * the staff SOW — and the customer walks the journey in its signed-off order:
 *
 *   welcome → residency → domain-verify → subscribe → payment → activation
 *
 *   (ADR-073 Amendment 6, 12 October 2026 — tighter sign-up: profile, persona and brand left this
 *   journey for the Launchpad's Business profile and Dashboard look steps, ADR-127.)
 *
 *   · BEFORE PAYMENT nothing account-bearing exists (Jeff: nothing is saved "UNTIL a commercial
 *     commitment has been made"). `residency` shows the form's country read-only; `domain-verify`
 *     is the AUTHORISATION of the form's website, recorded on the lead (no Domain row); `subscribe`
 *     records the plan on the lead. The lead is the pre-payment resume store: the same email
 *     returning later picks up where it stopped.
 *   · PAYMENT is the commitment: the step's port calls the account service's payment-confirm, which
 *     creates the login, account, Domain, subscription and routing rows at once, deletes the lead,
 *     sends the activation email, and returns a short-lived completion token. Its result is FED to
 *     the engine's evidence (ADR-107 A1.1 — the Done-gate is given the Domain it just created).
 *   · AFTER PAYMENT the same engine instance carries on to `activation` (the success screen) —
 *     persisting through the token-authorised `POST /signup/applications/complete-step`, whose
 *     terminal step runs the sign-up finaliser (the answers land on the Domain; the snippet is
 *     emailed). Done closes the modal and returns the visitor to the site (mount-owns-exit). The
 *     onboarding hub remains the safety net for anything left unfinished (Jeff's rule 2).
 *
 * The engine's own "Step N of M — label" is the only step counter on this page.
 *
 * Every step fits the modal's frame without scrolling (Jeff: "No scrolling, No opportunity for
 * confusion") — `presentation="embedded"`, measured at desktop and mobile size by
 * `e2e/sow-matrix-no-scroll.mjs`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ENV } from '../amplify-config';
import { assessSowMount } from '../obw/fep';
// Stage 4 (13 Aug 2026) — `data.error` is a routing key, never copy. See the
// module header for the demo failure this closes.
import { resolveSignupError, checkBusinessEmail } from '../lib/signup-errors';
// Stage 3 (13 Aug 2026) — the same normaliser `domain-verify` uses, so the value
// captured here and the value confirmed there can never disagree.
import { normaliseDomainInput } from '../lib/domain-normalise';
import { readZoneGate, sowGateBase, useZoneMap } from '../lib/zone-map';
import {
  RESIDENCY_HELPER,
  RESIDENCY_LABEL,
  closedGateLine,
  consequenceLine,
  zoneFor,
} from '../obw/residency';
import {
  COUNTRY_NOT_AVAILABLE_LINE,
  COUNTRY_NOT_LISTED,
  signupCountryOptions,
} from '../lib/signup-countries';
import { type SowEmbedOutbound } from '../lib/sow-embed-bridge';
// ADR-073 Am.5 §A5.6 — the ENGINE, mounted account-less: the SOW Matrix's own `sign-up` journey.
import { OBWWizard } from '../obw/OBWWizard';
import type { ObwStepId } from '../obw/journeys';
import type { PaymentConfirm, PaymentPort } from '../obw/payment';
import { usePostToShell, MARKETING_SITE_URL } from '../lib/use-post-to-shell';
// ADR-104 Layer 1 — the framed embed reports the three failures only it can see
// (a failed submit, an erroring Turnstile, an unreadable gate). See the module
// header for why no other layer can observe any of them.
import { beaconEmbedFailure, cappedBeacon } from '../lib/embed-health-beacon';
import {
  loadRateLimitState,
  saveRateLimitState,
  recordAttempt,
  canAttempt,
  remainingSeconds,
  type SowRateLimitState,
} from '../lib/sow-rate-limit';

// Phase 5c.5 (ADR-030 D6) — Cloudflare Turnstile site key, injected by Vite at build time.
//
// ⛔ THE TEST-KEY FALLBACK IS DEV-SERVER ONLY (30 September 2026). It used to apply to every
// build, so the production bundle shipped Cloudflare's always-pass test key from 27 May 2026
// until 30 September 2026 — the secret GitHub never held expanded to "", and `||` quietly
// substituted the test key. `import.meta.env.DEV` is a compile-time `false` in `vite build`, so
// the literal is dead code and is eliminated: a production bundle cannot contain a test key,
// and `deploy.yml` now fails the deploy if one does (and if the real key is empty).
const TURNSTILE_SITE_KEY_PORTAL =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY_PORTAL as string | undefined) ||
  (import.meta.env.DEV ? '1x00000000000000000000AA' : '');
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
// window.turnstile + TurnstileApi typed in vite-env.d.ts

interface SignupResponse {
  message?: string;
  error?: string;
}

// ADR-092 D5 — password-manager opt-out for the SIGN-UP and BETA fields. These
// are new-account capture forms, NOT a login: a password manager offering to
// fill/save here is noise at best, and the auto-submit variant is the Dashlane
// wrong-account vector this build removes. `autoComplete="off"` plus the vendor
// opt-out data-attributes (1Password / LastPass / Dashlane) keep the managers
// out. NOTE: the /embed/sign-in form is a real login and deliberately does NOT
// use this — there we WANT the manager to help the visitor into their account.
const PM_OPTOUT = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-form-type': 'other',
} as const;

/**
 * The in-frame "Sign in" affordance. NOT a link to the top-level login page
 * (that was the Dashlane vector) — it asks the shell to swap the modal to the
 * /embed/sign-in embed IN PLACE via the validated bridge (nexa-sow:switch).
 */
function SignInSwitchButton({
  post,
  children,
  style,
}: {
  post: (msg: SowEmbedOutbound) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={() => post({ type: 'nexa-sow:switch', to: 'signin' })}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--accent)',
        cursor: 'pointer',
        padding: 0,
        font: 'inherit',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function SowEmbedPage() {
  const [searchParams] = useSearchParams();
  const { post, framed } = usePostToShell();
  const contentRef = useRef<HTMLDivElement>(null);

  // §A2.2 — the embed is an invisible sandbox, not a navigable page. An
  // unframed (top-level) production visit is sent to the marketing site,
  // where the SOW modal lives. Dev keeps top-level access for local work.
  useEffect(() => {
    if (!framed && !import.meta.env.DEV) {
      window.location.replace(MARKETING_SITE_URL);
    }
  }, [framed]);

  // Auto-resize (§A2.3) — observe the content's natural height and report it
  // to the shell so the framed journey reads as one continuous modal.
  //
  // ⭐ NO SCROLLBAR WHILE THE FRAME CATCHES UP (ADR-073 Am.5 §A5.6 — "No scrolling"). The shell
  // animates the iframe to the reported height (`transition: height 0.24s`), so for a quarter of a
  // second after a TALLER step appears its content overflows the frame — measured live on www,
  // 3–10 px — and a browser with always-visible scrollbars (Windows) flashes one. So overflow is
  // hidden while the frame settles, and scrolling is allowed again ONLY if the content still
  // overflows once it has: content taller than the shell's clamp must never be trapped.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    let raf = 0;
    let settleTimer = 0;
    const SETTLE_MS = 500; // > the shell's 240 ms transition, with room for the postMessage hop
    const settle = () => {
      root.style.overflowY = 'hidden';
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        root.style.overflowY = root.scrollHeight > window.innerHeight + 1 ? 'auto' : 'hidden';
      }, SETTLE_MS);
    };
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        post({ type: 'nexa-sow:resize', height: Math.ceil(el.scrollHeight) });
        settle();
      });
    });
    ro.observe(el);
    // The frame itself resizing (the shell's transition, or the visitor's window) re-runs the check.
    window.addEventListener('resize', settle);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', settle);
      ro.disconnect();
      root.style.overflowY = '';
    };
  }, [post]);

  // D1 — `?plan=` is no longer read: the plan is chosen in the SOW. `?promo=` survives only for
  // the closed-gate beta-interest capture (ADR-080 Am.1 D3), never on the sign-up submission.
  const initialPromo = searchParams.get('promo') ?? '';
  // ADR-098 §2.4 — the marketing site's embed loader appends `?sid=<track.js
  // session token>` so a beta-interest lead can be linked to the journey that
  // produced it. Whitelisted server-side; absent (older loader) ⇒ no journey.
  const sessionParam = (searchParams.get('sid') ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 128);

  const [businessEmail, setBusinessEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  /**
   * ADR-105 D1 Amendment 2 (13 August 2026) — the website domain, captured HERE.
   *
   * D1 originally deferred domain capture entirely to the `domain-verify` step,
   * on the reasoning that this form is deliberately minimal. Live stakeholder
   * UAT overturned the timing: the tester stopped mid-flow and asked which
   * domain the sign-up was even for, and where to enter it. D1's CORE RULE is
   * untouched — the domain is never derived from the business-email domain
   * (`owner@holdingco.com` monitoring `retailbrand.com` is ordinary).
   *
   * ⚠ THIS IS A HINT, NOT A COMMIT. No Domain row is minted here, and that is
   * structural rather than a choice: a Domain row needs an `accountId` to hang
   * off, this form creates no account, and D2's rollback-release contract is
   * keyed on `accountId`. So the value is carried forward and PRE-FILLS
   * `domain-verify`, where the subscriber's explicit confirm still drives the
   * existing uniqueness-guarded mint. D2 is therefore unchanged: an abandoned
   * sign-up still releases nothing, because nothing was minted yet.
   */
  const [websiteDomain, setWebsiteDomain] = useState('');
  const [domainWarning, setDomainWarning] = useState('');
  /** Stage 4 — the on-blur business-email hint. Separate from `error` so the
   *  submit-time alert box and this field-level nudge never overwrite each
   *  other. */
  const [emailWarning, setEmailWarning] = useState('');
  // Optional — see the field markup below.
  const [ownerName, setOwnerName] = useState('');
  /**
   * ⭐⭐ ADR-124 D1 — THE RESIDENCY ANSWER. Was a three-country select whose value the server
   * then reduced with `… ? j : 'AU'` (F1). It is now every country the platform places, from the
   * SERVED map, with the consequence line beneath it; the value goes in the body and the
   * sow-gate Worker routes the submission to the zone that serves it (I4).
   */
  const [jurisdiction, setJurisdiction] = useState('');
  const zoneMap = useZoneMap();
  // D3 — the public sign-up's shortlist, filtered to what the served map actually places.
  const countryChoices = useMemo(() => (zoneMap ? signupCountryOptions(zoneMap) : []), [zoneMap]);
  /** D3 — "my country isn't listed": no zone, no account, the interest path only. */
  const notListed = jurisdiction === COUNTRY_NOT_LISTED;
  const derivedZone = jurisdiction && !notListed && zoneMap ? zoneFor(zoneMap, jurisdiction) : null;
  /**
   * ADR-124 D6 — the gate is evaluated AFTER the residency answer, against the DERIVED zone: a
   * UK business is checked against the US gate, because that is where its account would live.
   * `closed` swaps the submit into the existing lead capture, stored in THAT zone.
   */
  const [zoneGate, setZoneGate] = useState<'unknown' | 'checking' | 'open' | 'closed'>('unknown');
  const zoneCheckRef = useRef(0);
  const chooseJurisdiction = useCallback((code: string) => {
    setJurisdiction(code);
    const z = code && code !== COUNTRY_NOT_LISTED && zoneMap ? zoneFor(zoneMap, code) : null;
    const ticket = ++zoneCheckRef.current;
    if (!z) { setZoneGate('unknown'); return; }
    setZoneGate('checking');
    void readZoneGate('sow', z.zone).then((mode) => {
      if (ticket === zoneCheckRef.current) setZoneGate(mode);
    });
  }, [zoneMap]);
  const [leadRegistered, setLeadRegistered] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /**
   * ADR-073 Am.5 §A5.6 — `details` is the sign-up form below; `journey` is the SOW Matrix's own
   * `sign-up` journey (the engine), mounted once the form has written the lead.
   */
  const [phase, setPhase] = useState<'details' | 'journey'>('details');
  /** The lead the form created or resumed (the unguessable capability the pre-payment steps carry). */
  const [leadId, setLeadId] = useState('');
  /** What the journey opens with: the form's answers, plus a resumed lead's plan (see `startJourney`). */
  const [journeySeed, setJourneySeed] = useState<{
    initialStepData: Partial<Record<ObwStepId, Record<string, unknown>>>;
    initialStepId?: ObwStepId;
  } | null>(null);
  /** Remounts the engine on "Start again" — a fresh journey for a fresh lead. */
  const [journeyKey, setJourneyKey] = useState(0);
  /**
   * The payment-confirm's result — the account the journey now carries on against, the evidence the
   * engine's Done-gate is fed (ADR-107 A1.1), and the completion token for the post-payment steps.
   */
  const [paid, setPaid] = useState<{ accountId: string; tier: string; completionToken?: string } | null>(null);

  // ADR-080 Amendment 1 — the SOW availability gate. Read the Worker's public
  // state to decide whether to show the sign-up journey (open) or the
  // beta-interest gate (closed). This is UX ONLY — enforcement is server-side
  // (edge Worker + signup-api 403). Client FAILS CLOSED too: an unreadable gate
  // shows the beta form, never the (server-blocked) journey.
  const [gateState, setGateState] = useState<'loading' | 'open' | 'closed'>('loading');

  // ADR-105 §D3 (closed 20 Jul 2026 by live measurement). The reported "~5-minute
  // lag" between toggling the gate and the website reflecting it is NOT
  // propagation: measured live, the Worker/KV flip is sub-second (6/6 toggles,
  // 0.73–1.51s) and the browser observes it in ≤2.6s. The real mechanism is HERE
  // — the gate was read ONCE per mount and never revalidated, so an embed that
  // stays mounted across a gate change (the modal left open) showed its
  // mount-time state INDEFINITELY. Proven live: with the gate closed for ~2 min,
  // a mounted embed still rendered the sign-up journey while a fetch in the same
  // browser returned `closed`. "5 minutes" was just however long elapsed before a
  // reload — the staleness was unbounded, not timed.
  //
  // So: revalidate when the embed regains visibility/focus, and on a slow tick
  // while visible, which bounds staleness to ~60s for an idle mount and 0 for a
  // fresh one. `gateState` drives the WHOLE subtree, so a flip unmounts whichever
  // form is on screen and discards anything typed into it — therefore a
  // revalidation NEVER applies once the visitor has started entering data. That
  // deferral is safe because enforcement is server-side and fails closed: a
  // stale-open form that is submitted after the gate shuts is rejected with a 403.
  const gateDirtyRef = useRef(false);
  useEffect(() => {
    const markDirty = () => { gateDirtyRef.current = true; };
    // Capture phase, on the document: catches typing in EITHER face (the journey
    // form or BetaGate's) without threading a prop through the child.
    document.addEventListener('input', markDirty, true);
    document.addEventListener('change', markDirty, true);
    return () => {
      document.removeEventListener('input', markDirty, true);
      document.removeEventListener('change', markDirty, true);
    };
  }, []);

  const mountedRef = useRef(true);
  // Set true IN the effect, not only at init: React StrictMode (dev) runs mount → cleanup → mount, and
  // a cleanup-only effect left this false for good, so every gate read was discarded and the dev
  // embed sat on "Loading…" forever (found 5 October 2026 by the no-scroll harness; production,
  // which mounts once, was unaffected).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ADR-104 Layer 1 — has the current gate-read failure streak already been
  // beaconed? See applyUnreadable in readGate for why this is one-per-streak.
  const gateUnreadableReportedRef = useRef(false);

  /** Read the gate. `force` (mount) always applies; a revalidation defers while
   *  the visitor is mid-form. FAILS CLOSED on any error or ambiguity.
   *
   *  ⭐ ADR-104 LAYER 1 — THE DISTINCTION THIS FUNCTION NOW MAKES.
   *  Every path below that shows the beta face calls `apply('closed')`, and until
   *  ADR-104 that was the WHOLE story — which meant "the gate is deliberately
   *  closed" and "we could not find out whether the gate is open" produced
   *  byte-identical behaviour and byte-identical silence. The first is normal
   *  operation. The second is an outage in which visitors who could have paid are
   *  shown a waiting list, and it reports nothing, anywhere: the fetch failed on
   *  the client, so there is no 5xx; no sign-up was attempted, so there is no
   *  server log; a form still renders, so the synthetic probe passes.
   *
   *  So the four closed-paths are now split by CAUSE, and only the three that are
   *  READ FAILURES beacon `gate-unreadable`. A legitimately closed gate
   *  (`mode !== 'open'` on a clean 200) beacons NOTHING — it is not a fault, and
   *  alerting on it would fire continuously for as long as sign-ups are
   *  deliberately shut, which is the state the platform is in today. That would
   *  train the operator to ignore the row and take the blind spot straight back.
   */
  const readGate = useCallback(async (force: boolean) => {
    const url = ENV.SOW_GATE_STATE_URL;
    const apply = (next: 'open' | 'closed') => {
      if (!mountedRef.current) return;
      if (!force && gateDirtyRef.current) return; // never discard typed input
      setGateState(next);
    };
    /**
     * Closed BECAUSE the read failed — the reportable case.
     *
     * ⚠ ONE BEACON PER FAILURE STREAK, NOT ONE PER READ. `readGate` is also on a
     * 60-second revalidation tick, so an unreadable gate would otherwise emit a
     * beacon every minute from every mounted embed for as long as the outage
     * lasts — a KV write per tab per minute, on the one code path whose job is to
     * stay cheap. And it would buy nothing: the alert is CRITICAL at a count of
     * one, so the 40th beacon changes no decision. The streak flag resets on the
     * next successful read, so a fault that recovers and returns reports again.
     */
    const applyUnreadable = () => {
      if (!gateUnreadableReportedRef.current) {
        gateUnreadableReportedRef.current = true;
        void beaconEmbedFailure('sow', 'gate-unreadable');
      }
      apply('closed');
    };
    // No URL configured at all. This is a BUILD defect (the Vite var is unset),
    // not a runtime blip — and it is the exact shape of the ADR-105 unwired-env
    // failure, where every human-visible surface agreed the gate was open while
    // the only consumer that mattered was reading nothing. It fails closed AND
    // reports, so a mis-wired deploy is loud rather than quietly revenue-negative.
    // ⚠ NOT RETRIED — a missing URL is a build defect, final on the first look.
    if (!url) { applyUnreadable(); return; }

    /** One attempt. `null` ⇒ the question went unanswered (retryable). */
    const attemptRead = async (): Promise<'open' | 'closed' | null> => {
      try {
        // `no-store` belt-and-braces: the Worker already sends it (measured — no
        // Age/CF-Cache-Status on the response), this just removes any doubt.
        const r = await fetch(url, { method: 'GET', cache: 'no-store' });
        if (r.status !== 200) return null;
        const d = (await r.json()) as { mode?: string };
        if (typeof d?.mode !== 'string') return null;
        return d.mode === 'open' ? 'open' : 'closed';
      } catch {
        // Network error, CORS rejection, or unparseable JSON body. All three mean
        // the question went unanswered.
        return null;
      }
    };

    /**
     * ⭐⭐ THE STAKEHOLDER-DEMO FAILURE THIS CLOSES (13 August 2026).
     *
     * Failing closed is correct and is UNCHANGED. What was wrong is that ONE
     * failed fetch was enough: a visitor who should have seen the paid sign-up
     * journey was silently shown the 3-field BETA-INTEREST form instead — a
     * different door, with no domain field and no error — and nothing on screen
     * said anything had gone wrong. That is what a paying stakeholder was shown
     * during a live demo. The Worker is hand-deployed to one `workers.dev`
     * endpoint with no redundancy (ADR-104 §A3.7), so a cold start or a brief
     * Cloudflare hiccup is an ordinary event.
     *
     * Two retries with 400/800ms backoff self-heal a blip in under 1.3s —
     * invisibly, because `gateState` is still `'loading'` and the branded
     * loading card is already on screen for the whole window.
     *
     * ⚠ ONLY A FAILED READ IS RETRIED. A clean 200 saying `closed` is a real
     * answer and returns immediately — re-asking it would add the whole ladder's
     * latency to the platform's normal resting state (sign-ups deliberately shut).
     *
     * ⭐ THE BEACON MOVED WITH IT, AND THAT SHARPENS THE ADR-104 SIGNAL. It now
     * fires only once every attempt has failed, so `gate-unreadable` means "the
     * gate was genuinely unreachable", not "one packet was lost". A fault that
     * self-heals no longer pages anyone.
     */
    const RETRY_BACKOFF_MS = [400, 800];
    for (let attempt = 0; attempt <= RETRY_BACKOFF_MS.length; attempt++) {
      const verdict = await attemptRead();
      if (verdict) {
        // A clean 200 answered the question — this is what ends a failure streak.
        gateUnreadableReportedRef.current = false;
        apply(verdict);
        return;
      }
      if (!mountedRef.current) return; // unmounted mid-ladder — stop
      const backoff = RETRY_BACKOFF_MS[attempt];
      if (backoff !== undefined) await new Promise((r) => setTimeout(r, backoff));
    }
    applyUnreadable();
  }, []);

  useEffect(() => { void readGate(true); }, [readGate]);

  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === 'visible') void readGate(false);
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    const tick = setInterval(revalidate, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
      clearInterval(tick);
    };
  }, [readGate]);

  // FEP (ADR-080) — this embed route IS the FEP's UNAUTHENTICATED public mount.
  // The FEP terminates public entry: trust is established BY PAYMENT (card-first,
  // ADR-073), there is NO Nexa consultant on this mount (nexaConsultant === null),
  // and provisioning fires on payment-confirm. Resolving the context here makes
  // the public entry an explicit FEP mount, and is the anchor for the perimeter
  // controls below. Journey stays mount-blind — the difference is DATA, not code.
  const fep = useMemo(() => assessSowMount({}), []);

  // Perimeter Layer 1 (ADR-080 §4.3) — LINEAR escalating rate-limit + countdown on
  // the submit that triggers a Lambda (POST /signup/applications). Keyed to timing,
  // not identity; PERSISTED so a reload cannot reset the cooldown (the Lambda-cost
  // protection). Deterministic and cheap; the real anti-pentest wall is the edge
  // (Layer 0) + the server circuit-breaker (Layer 2).
  const [rlState, setRlState] = useState<SowRateLimitState>(() => loadRateLimitState());
  const [nowTs, setNowTs] = useState<number>(() => Date.now());
  const cooldownRemaining = remainingSeconds(rlState, nowTs);
  const inCooldown = cooldownRemaining > 0;
  // ⭐ THE COUNTDOWN IS SHOWN ONLY WHEN SOMETHING IS ACTUALLY BLOCKED — never over
  // the visitor's own in-flight submit.
  //
  // ⚠ `recordAttempt` stamps the attempt BEFORE the fetch (deliberately — the cost
  // being throttled is the invocation, and a reload must not un-count it). With
  // RL_FREE_ATTEMPTS = 1 that means the SECOND submit arms a 5s cooldown the instant
  // it is permitted, so `inCooldown` flipped true while that very submit was still on
  // the wire and the abuse modal covered the form for the whole request. A visitor
  // whose first sign-up succeeded on their second interaction — a mistyped email, a
  // "that email already exists → sign in" retry, or simply signing a second business
  // up — was shown "To keep sign-up secure we space out repeated attempts" WHILE the
  // attempt they were being asked to wait for was going through. Reproduced live on
  // the apex, 21 July 2026: countdown box + "Please wait 4s…" over a submit that was
  // ALLOWED and completing.
  //
  // ⭐ THIS WEAKENS NOTHING. The escalation ladder, its persistence, the idle decay
  // and the submit-time `canAttempt` gate are all untouched — a blocked attempt still
  // meets the modal, and a reload during the cooldown still lands on it (the mount is
  // not `busy`). What changes is only that the perimeter stops accusing a request it
  // has just permitted. (ADR-080 §4.3 — Layer 1 scope unchanged; enforcement remains
  // the edge + server-side circuit-breaker.)
  const showCooldown = inCooldown && !busy;

  // Tick the countdown while a cooldown is active; also survives a reload because
  // rlState is re-hydrated from sessionStorage on mount (loadRateLimitState).
  useEffect(() => {
    if (!inCooldown) return;
    const t = setInterval(() => setNowTs(Date.now()), 250);
    return () => clearInterval(t);
  }, [inCooldown]);

  // Discourage a reload-to-bypass during the cooldown (the persisted state already
  // makes it futile; this is the "modal blocks reload" belt, §4.3).
  useEffect(() => {
    if (!inCooldown) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [inCooldown]);

  // Phase 5c.5 (ADR-030 D6 + D7.2) — Turnstile widget state. Submit handler
  // reads the token via ref so it always sees the latest value (D7.2 race
  // condition fix); submit button is disabled until the widget's onSuccess
  // callback populates the token.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => { turnstileTokenRef.current = turnstileToken; }, [turnstileToken]);

  // ADR-104 Layer 1 — capped because Cloudflare's `error-callback` auto-retries
  // on some network-error classes. One reporter per mount (see cappedBeacon).
  const reportTurnstileRef = useRef(cappedBeacon());

  // Load Turnstile script + render widget once the sign-up form is shown (gate
  // open). Guarded on gateState so the widget renders after the form mounts —
  // when the gate is closed the sign-up form (and its container) never render.
  useEffect(() => {
    if (gateState !== 'open' || phase !== 'details') return;
    function renderWidget() {
      if (!turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current) return;
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY_PORTAL,
        theme: 'dark',
        callback: (token: string) => setTurnstileToken(token),
        // ADR-104 Layer 1 — the second probe-green blind spot. The form is fully
        // rendered and the submit button will now never enable, so the visitor
        // sees a working form they cannot submit. The synthetic probe asserts a
        // `<form>` exists inside the frame and therefore passes.
        //
        // ⚠ `expired-callback` deliberately does NOT report. Turnstile tokens
        // have a short TTL by design, so expiry is what happens to anyone who
        // reads the terms link before submitting — normal, self-healing (the
        // widget re-challenges), and not a fault. Reporting it would bury the
        // real signal under routine noise.
        'error-callback': () => {
          reportTurnstileRef.current('sow', 'turnstile-error');
          setTurnstileToken(null);
        },
        'expired-callback': () => setTurnstileToken(null),
      });
    }

    if (window.turnstile) { renderWidget(); return; }

    let existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!existing) {
      existing = document.createElement('script');
      existing.id = TURNSTILE_SCRIPT_ID;
      existing.src = TURNSTILE_SCRIPT_SRC;
      existing.async = true;
      existing.defer = true;
      document.head.appendChild(existing);
    }
    existing.addEventListener('load', renderWidget);
    return () => existing?.removeEventListener('load', renderWidget);
  }, [gateState, phase]);

  /** Leaving the details screen unmounts the widget's container — forget the widget, so returning
   *  (to change the website, or to start again) renders a fresh challenge for the new lead. */
  function forgetTurnstile() {
    try { window.turnstile?.remove(turnstileWidgetIdRef.current ?? undefined); } catch { /* not present */ }
    turnstileWidgetIdRef.current = null;
    setTurnstileToken(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!jurisdiction) { setError('Please select the country your business is registered in.'); return; }
    if (!businessEmail.trim()) { setError('Please enter your business email.'); return; }
    if (!businessName.trim()) { setError('Please enter your business name.'); return; }
    // ADR-105 D1 Am.2 — required, and normalised with the SAME function
    // `domain-verify` uses, so a value accepted here is never rejected there.
    const normalisedDomain = normaliseDomainInput(websiteDomain);
    if (!websiteDomain.trim()) { setError('Please enter your website domain.'); return; }
    if (!normalisedDomain) {
      setError('Please enter a valid website domain, e.g. yourbusiness.com.');
      return;
    }

    // D7.2 — read from ref so we always see the latest token, not a stale
    // closure value captured at handler creation.
    const tsToken = turnstileTokenRef.current;
    if (!tsToken) { setError('Please complete the verification challenge.'); return; }

    // ADR-124 D6 — the derived zone is closed: register interest instead, in THAT zone.
    // D3 — an unlisted country takes the same door, with no country declared (the lead lands
    // where pre-residency leads always have).
    if (zoneGate === 'closed' || notListed) {
      await registerInterest(tsToken, normalisedDomain.url);
      return;
    }

    // FEP invariant (ADR-080) — the public mount is the UNAUTH path: provisioning
    // is always payment-confirm, never a launch token. If this ever flips, the
    // wrong mount is wired; fail closed rather than mis-provision.
    if (fep.provisioning.trigger !== 'payment-confirm') {
      setError('Sign-up is temporarily unavailable. Please try again later.');
      return;
    }

    // Perimeter Layer 1 (ADR-080 §4.3) — re-read the persisted state (survives a
    // reload / another tab) and enforce the linear cooldown BEFORE hitting the
    // Lambda. A blocked attempt shows the countdown and burns zero core compute.
    const now = Date.now();
    const current = loadRateLimitState();
    if (!canAttempt(current, now)) {
      setRlState(current);
      setNowTs(now);
      return; // the countdown modal (rendered below) blocks the submit
    }
    // Count this attempt (a submit that reaches the network counts toward the
    // escalation, success or failure — the throttled cost is the invocation) and
    // persist it immediately so a reload cannot un-count it.
    const nextRl = recordAttempt(current, now);
    setRlState(nextRl);
    saveRateLimitState(nextRl);
    setNowTs(now);

    setBusy(true);
    try {
      // ADR-124 I4 — ONE public URL: the sow-gate Worker, which routes this to the zone that serves
      // `jurisdiction` and checks that zone's gate (kill-switch AND zone) on the way.
      const res = await fetch(`${sowGateBase()}/signup/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessEmail: businessEmail.trim().toLowerCase(),
          businessName: businessName.trim(),
          // `undefined` (not '') when blank, so the request body simply omits the
          // key rather than asserting an empty name — the server writes the
          // attribute only when a value arrives.
          ownerName: ownerName.trim() || undefined,
          // ADR-105 D1 Am.2 — the canonical form (`https://<bare-host>`), which
          // is exactly what `domain-verify` will render and what the mint would
          // store. Sending the raw input instead would let the pre-fill differ
          // from what the subscriber typed here, for no reason.
          websiteDomain: normalisedDomain.url,
          jurisdiction,
          // D1 — no plan, promo or card: those are chosen in the SOW, and signup-api refuses them.
          turnstileToken: tsToken,
          // ADR-073 Am.5 — this page continues to the plan and payment screens; a page that cannot
          // (an older cached bundle) does not send this and is told to refresh.
          flow: 'card-first',
        }),
      });

      const data = (await res.json()) as SignupResponse & {
        leadId?: string;
        resumed?: { selectedTier?: string; commitment?: string; promoCode?: string; domainConfirmed?: boolean };
      };

      if (res.status === 201 && data.leadId) {
        // ⭐ A LEAD, NOT AN ACCOUNT. Nothing account-bearing exists yet; the journey comes next.
        setLeadId(data.leadId);
        forgetTurnstile();
        startJourney(normalisedDomain, data.resumed);
        return;
      }

      // ⭐⭐ THIS LINE IS THE CONFIRMED 13 AUGUST DEFECT. It read `data.error` —
      // the machine code — so a stakeholder was shown the literal string
      // `email_domain_blocked` in a persistent, non-dismissing alert box, while
      // the friendly sentence the server had ALREADY sent sat unread in
      // `data.message`. `resolveSignupError` has no branch that can return a raw
      // code (see lib/signup-errors.ts); 409 keeps its own copy because the
      // "please sign in" instruction is the point of that one.
      if (res.status === 409) {
        setError('An account with this email already exists. Please sign in.');
      } else if (res.status === 403 && data.error === 'sow_gate_closed') {
        // The derived zone closed after this page read it — swap to its lead capture, which
        // states why (I9) rather than showing an error the visitor did nothing to cause.
        setZoneGate('closed');
      } else {
        setError(resolveSignupError(data));
      }

      // ADR-104 Layer 1 — a real person filled this form in and did not get an
      // account. Report it.
      //
      // ⚠ 409 IS DELIBERATELY EXCLUDED. "This email already exists" is the
      // platform working correctly: the duplicate check fired and told the
      // visitor to sign in instead. Counting it would put a steady baseline of
      // non-faults under a CRITICAL-at-five alarm, and a threshold that trips on
      // normal operation is one the operator learns to ignore. Every OTHER
      // non-2xx is a genuine "wanted to sign up, couldn't": 400 (a validation
      // rule the form should have caught first), 403 (the gate shut mid-form),
      // 503 (the circuit-breaker), 500 (broken).
      if (res.status !== 409) void beaconEmbedFailure('sow', 'submit-error');

      // Reset the Turnstile widget on any error so the user gets a fresh
      // challenge for the retry (per ADR-030 D6).
      try { window.turnstile?.reset(turnstileWidgetIdRef.current ?? undefined); } catch { /* widget may not be present */ }
      setTurnstileToken(null);
    } catch {
      setError('Network error. Please check your connection and try again.');
      // The request never completed — no status, and therefore nothing on the
      // server side to alarm on. This branch is the ONLY witness to a DNS/TLS/
      // CORS/edge-403 failure between the visitor and signup-api, which is
      // precisely why Layer 2's Lambda alarms cannot replace Layer 1.
      void beaconEmbedFailure('sow', 'submit-error');
      try { window.turnstile?.reset(turnstileWidgetIdRef.current ?? undefined); } catch { /* noop */ }
      setTurnstileToken(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ADR-124 D6 — the closed-zone lead capture: the existing beta-interest door, now carrying the
   * declared country so the Worker stores the lead in THAT zone. Same fields the visitor already
   * typed; nothing is asked twice.
   */
  async function registerInterest(tsToken: string, domainUrl: string) {
    setBusy(true);
    try {
      const res = await fetch(`${sowGateBase()}/signup/beta-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessEmail: businessEmail.trim().toLowerCase(),
          businessName: businessName.trim(),
          domain: domainUrl,
          ownerName: ownerName.trim() || undefined,
          promoCode: initialPromo || undefined,
          sessionToken: sessionParam || undefined,
          // D3 — an unlisted country declares nothing (never a guessed zone).
          jurisdiction: notListed ? undefined : jurisdiction,
          turnstileToken: tsToken,
        }),
      });
      if (res.status === 200) {
        setLeadRegistered(true);
        post({ type: 'nexa-sow:success' });
        return;
      }
      setError('Something went wrong. Please try again.');
      void beaconEmbedFailure('sow', 'submit-error');
    } catch {
      setError('Network error. Please check your connection and try again.');
      void beaconEmbedFailure('sow', 'submit-error');
    } finally {
      try { window.turnstile?.reset(turnstileWidgetIdRef.current ?? undefined); } catch { /* noop */ }
      setTurnstileToken(null);
      setBusy(false);
    }
  }

  // Shared shell for every state — chromeless card, height observed for resize.
  // `logo: false` for the sign-up form: the marketing site's modal header already shows the
  // Nexanomics logo directly above this frame, and the form must fit without scrolling (§A5.6).
  function frame(children: React.ReactNode, opts: { logo?: boolean; className?: string } = {}) {
    return (
      <main className={`sow-embed-page${opts.className ? ` ${opts.className}` : ''}`}>
        <div className="sow-embed-content" ref={contentRef}>
          <div className="auth-card">
            {opts.logo !== false && (
              <div className="auth-logo-wrap">
                <img src="/nexa-logo.png" alt="Nexanomics" className="auth-logo" />
              </div>
            )}
            {children}
          </div>
        </div>
      </main>
    );
  }

  // ADR-124 D6 — the closed-zone lead was captured (in that zone): the existing confirmation.
  if (leadRegistered) {
    return (
      <main className="sow-embed-page">
        <div className="sow-embed-content" ref={contentRef}>
          <RegisteredCard post={post} />
        </div>
      </main>
    );
  }

  /**
   * Open the SOW Matrix's `sign-up` journey for this lead, seeded with what the visitor has already
   * given — so nothing is asked twice:
   *   · `residency`     — the form's country, shown READ-ONLY (ADR-124 D1: the answer exists and the
   *                       zone is already derived). Its account-less validator passes on the served map.
   *   · `domain-verify` — the form's website, pre-filled for the customer to confirm or edit.
   *   · `subscribe`     — a resumed lead's plan, term and promo.
   *   · `profile`       — the business name and email from the form (the hub does the same, #529).
   *   · `activation`    — where the snippet email goes (the step says so).
   * A RESUMED lead opens at the first pre-payment step it has not finished: the website still to
   * confirm, else the plan still to choose, else payment. A new lead opens at step 1 (ADR-107 A2.1).
   */
  function startJourney(
    site: { url: string; hostname: string },
    resumed?: { selectedTier?: string; commitment?: string; promoCode?: string; domainConfirmed?: boolean },
  ) {
    const email = businessEmail.trim().toLowerCase();
    const plan = resumed?.selectedTier
      ? {
        selectedTier: resumed.selectedTier,
        ...(resumed.commitment ? { commitment: resumed.commitment } : {}),
        ...(resumed.promoCode ? { promoCode: resumed.promoCode } : {}),
      }
      : undefined;
    const initialStepData: Partial<Record<ObwStepId, Record<string, unknown>>> = {
      residency: {
        jurisdiction,
        ...(derivedZone ? { zone: derivedZone.zone, zoneLabel: derivedZone.label } : {}),
      },
      'domain-verify': {
        // The bare hostname, as the visitor typed it — the step normalises it again on confirm.
        domainUrl: site.hostname,
        domainHostname: site.hostname,
        ...(resumed?.domainConfirmed ? { consented: true } : {}),
      },
      ...(plan ? { subscribe: plan } : {}),
      profile: { businessName: businessName.trim(), businessEmail: email },
      activation: { snippetEmailTo: email },
    };
    const initialStepId: ObwStepId | undefined = !resumed
      ? undefined
      : !resumed.domainConfirmed
        ? 'domain-verify'
        : !plan
          ? 'subscribe'
          : 'payment';
    setJourneySeed({ initialStepData, ...(initialStepId ? { initialStepId } : {}) });
    setPaid(null);
    setPhase('journey');
  }

  /** "Start again" — back to the form; submitting it again resumes or writes the lead afresh. */
  function restartDetails() {
    setPhase('details');
    setLeadId('');
    setJourneySeed(null);
    setPaid(null);
    setJourneyKey((k) => k + 1);
  }

  /** POST one public sign-up call through the sow-gate Worker (it zone-routes by `jurisdiction`). */
  async function postSignup(path: string, body: Record<string, unknown>): Promise<{ status: number; data: SignupResponse & Record<string, unknown> }> {
    const res = await fetch(`${sowGateBase()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, jurisdiction }),
    });
    const data = (await res.json().catch(() => ({}))) as SignupResponse & Record<string, unknown>;
    return { status: res.status, data };
  }

  /**
   * The engine's persistence port (ADR-107 §2.4 — the engine owns no transport; the mount does).
   *
   * BEFORE payment the journey is account-less, so "persist" means the LEAD (signup-api):
   *   · `domain-verify` — the website the customer CONFIRMED (the authorisation; payment provisions
   *                       the Domain from it). No Domain row is written.
   *   · `subscribe`     — the plan, term and promo (ADR-073 §2.6 drop-point: `card-abandoned`).
   * AFTER payment it means the new ACCOUNT, through the completion token (account service): profile,
   * persona, brand, and the terminal `activation`, which runs the finaliser server-side.
   * Best-effort by the engine's contract; a failure is logged and the server stays authoritative —
   * payment refuses an unconfirmed website, and the hub finishes any detail that did not land.
   */
  async function persistStep(stepId: ObwStepId, data: Record<string, unknown>): Promise<void> {
    try {
      if (paid) {
        if (!paid.completionToken) return;
        if (!['profile', 'persona', 'brand', 'activation'].includes(stepId)) return;
        const r = await postSignup('/signup/applications/complete-step', {
          token: paid.completionToken, stepId, data,
        });
        if (r.status !== 200) console.warn('sow-embed: post-payment step not saved', { stepId, status: r.status });
        return;
      }
      if (!leadId) return;
      if (stepId === 'domain-verify' && typeof data.domainUrl === 'string' && data.domainUrl) {
        const r = await postSignup('/signup/applications/progress', { leadId, websiteDomain: data.domainUrl });
        if (r.status !== 200) console.warn('sow-embed: website confirmation not recorded', { status: r.status });
      } else if (stepId === 'subscribe' && typeof data.selectedTier === 'string') {
        const r = await postSignup('/signup/applications/progress', {
          leadId,
          selectedTier: data.selectedTier,
          commitment: data.commitment,
          promoCode: data.promoCode ?? undefined,
        });
        if (r.status !== 200) console.warn('sow-embed: plan not recorded on the lead', { status: r.status });
      }
    } catch (err) {
      console.warn('sow-embed: step persistence failed', { stepId, error: (err as Error).message });
    }
  }

  /**
   * ⭐ THE COMMERCIAL COMMITMENT — the `payment` step's port (obw/payment.ts). The account service
   * confirms the payment (the stubbed seam, ADR-073 Am.1) and, only then, creates the login, the
   * account, the Domain and the subscription — all at once, in this country's zone — and sends the
   * activation email. Every refusal comes back in plain words with a way forward; "you haven't been
   * charged" is in each money-adjacent one.
   */
  async function confirmPayment(stepData: Record<string, unknown>): Promise<PaymentConfirm> {
    try {
      const { status, data } = await postSignup('/signup/applications/pay', {
        leadId,
        selectedTier: stepData.selectedTier,
        commitment: stepData.commitment,
        promoCode: stepData.promoCode ?? undefined,
      });
      if (status === 201 && typeof data.accountId === 'string' && data.accountId) {
        setPaid({
          accountId: data.accountId,
          tier: typeof data.tier === 'string' ? data.tier : String(stepData.selectedTier ?? ''),
          ...(typeof data.completionToken === 'string' ? { completionToken: data.completionToken } : {}),
        });
        post({ type: 'nexa-sow:success' });
        return { ok: true };
      }
      const code = typeof data.error === 'string' ? data.error : '';
      const recover = code === 'domain_already_registered' || code === 'domain_not_confirmed'
        ? 'domain-verify' as const
        : code === 'email_already_exists'
          ? 'sign-in' as const
          : code === 'lead_not_found' || code === 'sow_gate_closed'
            ? 'restart' as const
            : undefined;
      // A decline (402) or a duplicate (409) is the platform working as designed — the visitor is told
      // why and given a way forward. Every OTHER failure is a person who tried to pay and could not.
      if (status !== 402 && status !== 409) void beaconEmbedFailure('sow', 'submit-error');
      return { ok: false, message: resolveSignupError(data), ...(recover ? { recover } : {}) };
    } catch {
      void beaconEmbedFailure('sow', 'submit-error');
      return { ok: false, message: 'Network error. Please check your connection and try again. You haven’t been charged.' };
    }
  }

  const paymentPort: PaymentPort = {
    cta: 'Pay and create my account',
    confirm: confirmPayment,
    signIn: () => post({ type: 'nexa-sow:switch', to: 'signin' }),
    restart: restartDetails,
  };

  if (phase === 'journey' && journeySeed) {
    return (
      <main className="sow-embed-page sow-embed-journey">
        <div className="sow-embed-content" ref={contentRef}>
          <OBWWizard
            key={journeyKey}
            journeyId="sign-up"
            // Account-less until payment confirms (the StaffObwModal mount); then the account it made.
            accountId={paid?.accountId ?? ''}
            actor="subscriber"
            presentation="embedded"
            // ADR-077 §2 — this mount is UNAUTHENTICATED, and says so. Its trust is the FEP's verdict:
            // established by payment (`trust: { via: 'payment' }`, provisioning on payment-confirm).
            authenticated={false}
            fepTrust={fep.trust.via === 'payment' ? 'payment' : undefined}
            initialStepData={journeySeed.initialStepData}
            initialStepId={journeySeed.initialStepId}
            residency={{ map: zoneMap, stored: { jurisdiction } }}
            payment={paymentPort}
            onPersistStep={persistStep}
            // ⭐ ADR-107 A1.1 — the Done-gate is FED the payment's result: the Domain it created
            // (`domainCount` 1), the account, its tier and lifecycle. Without this the gate would ask
            // "does this account own a domain?" of an engine that cannot see the one just made.
            serverEvidence={paid
              ? { domainCount: 1, provisioned: true, tier: paid.tier, setupState: 'paid_pending_activation' }
              : undefined}
            // Once paid, the pre-payment steps are done for good: no Back into them (Pay again).
            lockedThroughStepId={paid ? 'payment' : undefined}
            // Mount-owns-exit (ADR-076 L1): Done — and any exit — closes the modal, back to the site.
            onComplete={() => post({ type: 'nexa-sow:close' })}
            onExit={() => post({ type: 'nexa-sow:close' })}
          />
        </div>
      </main>
    );
  }

  // ADR-080 Amendment 1 — while the gate state is loading, show a minimal branded
  // card (avoids flashing the sign-up form then swapping it for the gate).
  if (gateState === 'loading') {
    return frame(<p className="auth-description" aria-live="polite">Loading…</p>);
  }

  // Gate CLOSED — the entire public journey is unavailable. Show the beta-interest
  // gate (D4). Enforcement is server-side; this is the branded UX face of it.
  if (gateState === 'closed') {
    return (
      <main className="sow-embed-page">
        <div className="sow-embed-content" ref={contentRef}>
          <BetaGate promo={initialPromo} sid={sessionParam} post={post} />
        </div>
      </main>
    );
  }

  return frame(
    <>
      <div className="sow-embed-title-row">
        {/* "Create your account" — the modal header directly above already shows the Nexanomics logo. */}
        <h1 className="auth-title">Create your account</h1>
        {/* Sign-in sits beside the title in the frame (§A5.6 — one line saved; same control). */}
        <p className="sow-embed-signin">
          <span className="sr-only">Already have an account? </span>
          <SignInSwitchButton post={post}>Sign in</SignInSwitchButton>
        </p>
      </div>

      {/* D1 (Jeff, 5 October 2026) — where pricing used to sit: plans and payment are the SOW's. */}
      <p className="auth-description" data-testid="signup-next-screens">
        You’ll choose your plan and add payment in the next screens.
      </p>

      {error && <div className="auth-error" role="alert">{error}</div>}

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        {/* D1/D3 — the country comes FIRST: it decides where the account lives, and with the
            shortlist it decides whether there is an account at all. */}
        {/* ADR-124 D1/I9 — the residency question, its helper and its consequence, verbatim. */}
        <div className="form-field">
          <label htmlFor="signup-jurisdiction">{RESIDENCY_LABEL}</label>
          <select
            id="signup-jurisdiction"
            value={jurisdiction}
            onChange={(e) => chooseJurisdiction(e.target.value)}
            required
            disabled={busy || !zoneMap}
            aria-describedby="signup-jurisdiction-hint"
          >
            <option value="">— Select country —</option>
            {countryChoices.map((o) => (
              <option key={o.code} value={o.code}>{o.name}</option>
            ))}
            {/* D3 — never a default and never a guess: an unlisted country says so. */}
            <option value={COUNTRY_NOT_LISTED}>My country isn’t listed</option>
          </select>
          <span id="signup-jurisdiction-hint" className="form-field-hint">{RESIDENCY_HELPER}</span>
          {jurisdiction && !notListed && zoneMap && (
            <span className="form-field-consequence" data-testid="signup-residency-consequence">
              {consequenceLine(zoneMap, jurisdiction)}
            </span>
          )}
          {notListed && (
            <span className="form-field-consequence" role="status" data-testid="signup-country-not-available">
              {COUNTRY_NOT_AVAILABLE_LINE} Register your interest and we’ll let you know when it is.
            </span>
          )}
          {zoneGate === 'closed' && derivedZone && (
            <span className="form-field-consequence" role="status" data-testid="signup-zone-closed">
              {closedGateLine(derivedZone.label)}
            </span>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="signup-email">Business email</label>
          <input
            id="signup-email"
            type="email"
            value={businessEmail}
            onChange={(e) => { setBusinessEmail(e.target.value); setEmailWarning(''); }}
            // Stage 4 — tell them at BLUR, not after a full form + a solved
            // Turnstile challenge. Advisory: the server is the authority and
            // still runs the same list, so this never blocks the submit.
            onBlur={(e) => setEmailWarning(checkBusinessEmail(e.target.value) ?? '')}
            {...PM_OPTOUT}
            required
            disabled={busy}
            placeholder="you@yourcompany.com"
            aria-describedby={emailWarning ? 'signup-email-warning' : undefined}
          />
          {emailWarning && (
            <span id="signup-email-warning" className="form-field-error" role="alert">
              {emailWarning}
            </span>
          )}
        </div>

        {/* The two short fields share a row (§A5.6 — the form fits the frame without scrolling). */}
        <div className="form-row-2">
          <div className="form-field">
            <label htmlFor="signup-business">Business name</label>
            <input
              id="signup-business"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              {...PM_OPTOUT}
              required
              disabled={busy}
              placeholder="Acme Corp"
            />
          </div>

          {/* Personalisation (20 Jul 2026) — the activation email's greeting. Mirrors the BetaGate
              field (same label, placeholder, password-manager opt-out). ⚠ DELIBERATELY OPTIONAL — no
              `required`: every mandatory field on a paid sign-up is a point of abandonment, and the
              name improves one email. */}
          <div className="form-field">
            <label htmlFor="signup-owner-name">Your name</label>
            <input
              id="signup-owner-name"
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              {...PM_OPTOUT}
              disabled={busy}
              placeholder="Jane Doe"
            />
          </div>
        </div>

        {/* ADR-105 D1 Amendment 2 (13 August 2026) — the website domain.

            ⭐ REQUIRED, unlike "Your name" above, and the asymmetry is deliberate:
            a missing name costs one unaddressed email, whereas a missing domain
            is what the platform MONITORS — it is the subject of the whole
            subscription. Live UAT showed the cost of not asking is worse than
            the conversion cost of asking: the tester stopped and asked what the
            sign-up was even for.

            ⚠ NOT DERIVED FROM THE EMAIL — see the state declaration above. This
            is a captured hint that pre-fills `domain-verify`; the mint still
            happens there, on an explicit confirm. */}
        <div className="form-field">
          {/* "to monitor" carries what the hint below used to say, so the hint shows only for a warning
              (§A5.6 — the form fits the frame without scrolling). */}
          <label htmlFor="signup-domain">Website to monitor</label>
          <input
            id="signup-domain"
            type="text"
            inputMode="url"
            value={websiteDomain}
            onChange={(e) => { setWebsiteDomain(e.target.value); setDomainWarning(''); }}
            onBlur={(e) => setDomainWarning(
              e.target.value.trim() && !normaliseDomainInput(e.target.value)
                ? 'That doesn’t look like a valid domain — try yourbusiness.com.'
                : '',
            )}
            {...PM_OPTOUT}
            required
            disabled={busy}
            placeholder="yourbusiness.com"
            aria-describedby={domainWarning ? 'signup-domain-warning' : undefined}
          />
          {domainWarning && (
            <span id="signup-domain-warning" className="form-field-error" role="alert">
              {domainWarning}
            </span>
          )}
        </div>

        {/* Phase 5c.5 (ADR-030 D6) — Cloudflare Turnstile widget */}
        <div
          ref={turnstileContainerRef}
          className="sow-embed-turnstile"
          style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}
          aria-label="Verification challenge"
        />

        <button
          type="submit"
          className="btn-primary"
          disabled={busy || !turnstileToken || inCooldown || zoneGate === 'checking'}
          aria-disabled={busy || !turnstileToken || inCooldown || zoneGate === 'checking'}
          style={{ width: '100%' }}
        >
          {/* ADR-124 D6 — a closed zone turns this into the existing lead-capture action. */}
          {zoneGate === 'closed' || notListed
            ? (busy ? 'Sending…' : 'Register your interest')
            : busy
              ? 'Saving…'
              : inCooldown
                ? `Please wait ${cooldownRemaining}s…`
                // ADR-073 Am.5 — nothing is created here; the SOW journey (and, at payment, the account) is next.
                : 'Continue'}
        </button>
      </form>

      {/* Perimeter Layer 1 (ADR-080 §4.3) — the countdown modal. It BLOCKS any
          further submit during the linear cooldown (the submit is also disabled
          above) and, with the persisted timing state + beforeunload guard, a
          reload cannot escape it. Timing-keyed: it shows only a countdown, never
          anything about the caller. */}
      {showCooldown && (
        <div
          className="obw-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-label="Please wait before trying again"
        >
          <div className="obw-modal" style={{ maxWidth: 420, textAlign: 'center' }}>
            <div className="obw-body">
              <h2 className="obw-title">Just a moment</h2>
              <p className="obw-subtitle">
                To keep sign-up secure we space out repeated attempts. You can try again in{' '}
                <strong style={{ color: 'var(--text)' }}>{cooldownRemaining}s</strong>.
              </p>
              <div
                className="obw-ai-status"
                role="timer"
                aria-live="polite"
                style={{ justifyContent: 'center', fontSize: '2rem', fontWeight: 700, color: 'var(--text)' }}
              >
                {cooldownRemaining}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The terms, at 14px — the org floor (this line was 13px). */}
      <p className="portal-aux-links sow-embed-aux">
        By continuing you agree to Nexanomics{' '}
        <a href="https://nexanomics.com/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://nexanomics.com/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>.
      </p>
    </>,
    { logo: false, className: 'sow-embed-form' },
  );
}

/**
 * BetaGate — the SOW availability-gate face (ADR-080 Amendment 1, D4). Shown when
 * the public sign-up journey is closed. The whole journey is unavailable; this
 * captures beta interest instead (POST /signup/beta-interest, the one hardened
 * door that stays open when the gate is closed). Turnstile-verified. Carries the
 * `?promo` code (D3 — captured while gated, applied when the gate later opens).
 * Enforcement is server-side; this is UX only.
 *
 * Copy per A2.6 — an action the visitor takes (register / request a place),
 * never a promise of contact: submitting alerts STAFF only
 * (signup-interest-alert.ts); the applicant receives nothing, so "we'll be in
 * touch" / "keep me posted" would promise an update that never happens.
 */
function BetaGate({ promo, sid, post }: { promo: string; sid: string; post: (msg: SowEmbedOutbound) => void }) {
  const [businessName, setBusinessName] = useState('');
  const [domain, setDomain] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  useEffect(() => { turnstileTokenRef.current = turnstileToken; }, [turnstileToken]);

  // ADR-104 Layer 1 — same capped reporter as the sign-up form above. This form
  // matters just as much: while the gate is closed it is the ONLY public door on
  // the platform, so a Turnstile fault here means nobody can register interest
  // at all, and the probe (which only ever opens the modal) stays green.
  const reportTurnstileRef = useRef(cappedBeacon());

  useEffect(() => {
    if (done) return;
    function renderWidget() {
      if (!turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current) return;
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY_PORTAL,
        theme: 'dark',
        callback: (token: string) => setTurnstileToken(token),
        'error-callback': () => {
          reportTurnstileRef.current('sow', 'turnstile-error');
          setTurnstileToken(null);
        },
        'expired-callback': () => setTurnstileToken(null),
      });
    }
    if (window.turnstile) { renderWidget(); return; }
    let existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!existing) {
      existing = document.createElement('script');
      existing.id = TURNSTILE_SCRIPT_ID;
      existing.src = TURNSTILE_SCRIPT_SRC;
      existing.async = true;
      existing.defer = true;
      document.head.appendChild(existing);
    }
    existing.addEventListener('load', renderWidget);
    return () => existing?.removeEventListener('load', renderWidget);
  }, [done]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!businessName.trim()) { setError('Please enter your business name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    const tsToken = turnstileTokenRef.current;
    if (!tsToken) { setError('Please complete the verification challenge.'); return; }

    setBusy(true);
    try {
      // ADR-124 I4 — through the sow-gate Worker, like every public submission. This (kill-switch)
      // form declares no country, so the Worker captures the lead where leads always were.
      const res = await fetch(`${sowGateBase()}/signup/beta-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessEmail: email.trim().toLowerCase(),
          businessName: businessName.trim(),
          domain: domain.trim() || undefined,
          ownerName: ownerName.trim() || undefined,
          promoCode: promo || undefined,
          // ADR-098 §2.4 — attach the visitor's session so staff can see the
          // journey that produced the lead.
          sessionToken: sid || undefined,
          turnstileToken: tsToken,
        }),
      });
      if (res.status === 200) {
        setDone(true);
        post({ type: 'nexa-sow:success' });
        return;
      }
      setError('Something went wrong. Please try again.');
      // ADR-104 Layer 1 — a lead was typed in and lost. There is no 409-style
      // benign case on this route (it has no duplicate check — staff work the
      // queue), so EVERY non-200 here is a real failure worth reporting.
      void beaconEmbedFailure('sow', 'submit-error');
      try { window.turnstile?.reset(turnstileWidgetIdRef.current ?? undefined); } catch { /* noop */ }
      setTurnstileToken(null);
    } catch {
      setError('Network error. Please check your connection and try again.');
      void beaconEmbedFailure('sow', 'submit-error');
      try { window.turnstile?.reset(turnstileWidgetIdRef.current ?? undefined); } catch { /* noop */ }
      setTurnstileToken(null);
    } finally {
      setBusy(false);
    }
  }

  if (done) return <RegisteredCard post={post} />;

  return (
    <div className="auth-card">
      <div className="auth-logo-wrap">
        <img src="/nexa-logo.png" alt="Nexanomics" className="auth-logo" />
      </div>
      <h1 className="auth-title">Subscriptions aren't open yet</h1>
      <p className="auth-description">
        Unfortunately, subscriptions are currently closed. Register your interest and we'll let
        you know when subscriptions re-open.
      </p>

      {error && <div className="auth-error" role="alert">{error}</div>}

      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className="form-field">
          <label htmlFor="beta-business">Business name</label>
          <input
            id="beta-business"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            {...PM_OPTOUT}
            required
            disabled={busy}
            placeholder="Acme Corp"
          />
        </div>

        <div className="form-field">
          <label htmlFor="beta-domain">Website</label>
          <input
            id="beta-domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            {...PM_OPTOUT}
            disabled={busy}
            placeholder="acme.com"
          />
        </div>

        <div className="form-field">
          <label htmlFor="beta-name">Your name</label>
          <input
            id="beta-name"
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            {...PM_OPTOUT}
            disabled={busy}
            placeholder="Jane Doe"
          />
        </div>

        <div className="form-field">
          <label htmlFor="beta-email">Email</label>
          <input
            id="beta-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            {...PM_OPTOUT}
            required
            disabled={busy}
            placeholder="you@yourcompany.com"
          />
        </div>

        <div
          ref={turnstileContainerRef}
          style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}
          aria-label="Verification challenge"
        />

        <button
          type="submit"
          className="btn-primary"
          disabled={busy || !turnstileToken}
          aria-disabled={busy || !turnstileToken}
          style={{ width: '100%' }}
        >
          {busy ? 'Sending…' : 'Register your interest'}
        </button>
      </form>

      <p className="portal-aux-links" style={{ fontSize: '14px', color: 'var(--text-dim)' }}>
        Already have an account?{' '}
        <SignInSwitchButton post={post}>Sign in</SignInSwitchButton>
      </p>
    </div>
  );
}

/** The "you're registered" confirmation — shared by the kill-switch form and the closed-zone
 *  capture (ADR-124 D6), so both doors end on the same, already-approved words. */
function RegisteredCard({ post }: { post: (msg: SowEmbedOutbound) => void }) {
  return (
    <div className="auth-card">
      <div className="auth-logo-wrap">
        <img src="/nexa-logo.png" alt="Nexanomics" className="auth-logo" />
      </div>
      <h1 className="auth-title">You're registered</h1>
      <p className="auth-description">
        Thanks — we've recorded your interest. We'll let you know when subscriptions re-open.
      </p>
      <button
        type="button"
        className="btn-primary"
        style={{ width: '100%', marginTop: '12px' }}
        onClick={() => post({ type: 'nexa-sow:close' })}
      >
        Back to the site
      </button>
      <p className="portal-aux-links" style={{ fontSize: '14px', color: 'var(--text-dim)' }}>
        Already have an account?{' '}
        <SignInSwitchButton post={post}>Sign in</SignInSwitchButton>
      </p>
    </div>
  );
}
